/**
 * Crash reports (Sentry) and a handful of product events (PostHog), in the live build only, and only once their keys
 * are set (VITE_SENTRY_DSN, VITE_POSTHOG_KEY): a dev server, an e2e run or a build without keys sends nothing.
 * Privacy: no email, no name, no save is ever sent; a player is their random game id (the same one multiplayer uses).
 * PostHog captures only what's tracked here (no autocapture, no session recordings): the launch plan's funnel, game
 * opened → life started → first activity → coming back, and which places and jobs people actually use.
 * The SDKs load lazily after the game has started, so they never slow the first frame.
 */
type Props = Record<string, string | number | boolean | null | undefined>;

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com';
const RELEASE = (import.meta.env.VITE_BUILD as string | undefined) || 'dev';
const LIVE = import.meta.env.PROD && !/[?&](slot|mp|spawn)=/.test(location.search); // (a test URL on the live site: not a player)
const ENV = /\.pages\.dev$/.test(location.hostname) && location.hostname.split('.').length > 3 ? 'preview' : 'production';

let ph: { capture(e: string, p?: Props): void; register(p: Props): void } | null = null;
const queue: [string, Props | undefined][] = [];
let started = false;

/** the game's own random id for this player (no personal data) */
function anonId(): string | undefined {
  try { return localStorage.getItem('unfold.playerId') ?? undefined; } catch { return undefined; }
}

export function initTelemetry() {
  if (started || !LIVE) return;
  started = true;
  if (SENTRY_DSN) void import('@sentry/browser').then((S) => {
    S.init({
      dsn: SENTRY_DSN, release: RELEASE, environment: ENV,
      tracesSampleRate: 0, // (no personal data is sent by default)
      // (a lost connection to the multiplayer server is weather, not a bug)
      ignoreErrors: [/WebSocket/i, /ResizeObserver loop/i, /NetworkError/i, /Failed to fetch/i, /Load failed/i],
      beforeSend(ev) { if (ev.user) ev.user = { id: ev.user.id }; return ev; },
    });
    const id = anonId();
    if (id) S.setUser({ id });
  }).catch(() => { /* blocked by an ad blocker: fine */ });
  if (POSTHOG_KEY) void import('posthog-js').then(({ default: posthog }) => {
    const id = anonId();
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST, autocapture: false, capture_pageview: true, capture_pageleave: true,
      disable_session_recording: true, person_profiles: 'identified_only', persistence: 'localStorage',
      ...(id ? { bootstrap: { distinctID: id } } : {}),
    });
    posthog.register({ release: RELEASE, touch: document.body.classList.contains('touch') || matchMedia('(pointer: coarse)').matches });
    ph = posthog as unknown as typeof ph;
    for (const [e, p] of queue.splice(0)) ph!.capture(e, p);
  }).catch(() => { /* blocked: fine */ });
}

/** One product event (queued until PostHog has loaded; nothing at all outside the live build). */
export function track(event: string, props?: Props) {
  if (!LIVE || !POSTHOG_KEY) return;
  if (ph) ph.capture(event, props); else if (queue.length < 50) queue.push([event, props]);
}

/** A handled error worth knowing about (Sentry), e.g. a save that failed. */
export function report(err: unknown, context?: Props) {
  if (!LIVE || !SENTRY_DSN) return;
  void import('@sentry/browser').then((S) => S.captureException(err, { extra: context })).catch(() => {});
}
